import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Filter, HelpCircle, Inbox, MessageSquare, Search } from "lucide-react";
import { DateInputDE } from "../components/DateInputs";
import { api, errMsg } from "../lib/api";
import { Button, Card, ErrorBox, Input, Select, Spinner } from "../components/kd";
import { StatusBadge, UrgencyBadge } from "../components/Badges";
import { DRINGLICHKEIT, STATUS, fmtDate, fmtDateTime } from "../lib/format";

const KPIS: [string, string, any, string][] = [
  ["neu", "Neu", Inbox, "text-primary"],
  ["rueckfragen", "Rückfragen offen", HelpCircle, "text-query"],
  ["termine_woche", "Termine diese Woche", CalendarDays, "text-primary"],
  ["schmerzfaelle_offen", "Schmerzfälle offen", AlertTriangle, "text-danger"],
];

function Kpis({ k }: { k: any }) {
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      {KPIS.map(([key, label, Icon, cls]) => (
        <div key={key} className="kd-card p-4" data-testid={`kpi-${key}`}>
          <p className="flex items-center gap-2 text-sm text-text-muted"><Icon className={`h-4 w-4 ${cls}`} aria-hidden />{label}</p>
          <p className="mt-1 text-h1 font-semibold">{k ? k[key] : "–"}</p>
        </div>
      ))}
    </div>
  );
}

const EMPTY = { status: [] as string[], dringlichkeit: "", ueberweiser_id: "", von: "", bis: "", nur_meine: false, suche: "", seite: 1 };

function Filters({ f, setF, uebs }: { f: any; setF: (x: any) => void; uebs: any[] }) {
  const [open, setOpen] = useState(false);
  const toggle = (s: string) => setF({ ...f, seite: 1, status: f.status.includes(s) ? f.status.filter((x: string) => x !== s) : [...f.status, s] });
  return (
    <Card className="mb-4 !p-4">
      <div className="relative mb-3 md:hidden">
        <Search className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-text-muted" aria-hidden />
        <Input aria-label="Suche" placeholder="Patient, Fallnummer, Überweiser" className="pl-10" value={f.suche} onChange={(e) => setF({ ...f, suche: e.target.value, seite: 1 })} data-testid="filter-search-input-mobile" />
      </div>
      <button type="button" className="flex min-h-[44px] items-center gap-2 font-medium text-primary md:hidden" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="filter-toggle">
        <Filter className="h-4 w-4" aria-hidden />{open ? "Filter ausblenden" : "Filter anzeigen"}
      </button>
      <div className={`${open ? "block" : "hidden"} md:block`}>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <div className="relative hidden md:col-span-2 md:block">
          <Search className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-text-muted" aria-hidden />
          <Input aria-label="Suche" placeholder="Patient, Fallnummer oder Überweiser" className="pl-10" value={f.suche} onChange={(e) => setF({ ...f, suche: e.target.value, seite: 1 })} data-testid="filter-search-input" />
        </div>
        <Select aria-label="Dringlichkeit" options={DRINGLICHKEIT} placeholder="Alle Dringlichkeiten" value={f.dringlichkeit} onChange={(e) => setF({ ...f, dringlichkeit: e.target.value, seite: 1 })} data-testid="filter-dringlichkeit-select" />
        <Select aria-label="Überweiser" options={uebs.map((u) => [u.id, u.name]) as any} placeholder="Alle Überweiser" value={f.ueberweiser_id} onChange={(e) => setF({ ...f, ueberweiser_id: e.target.value, seite: 1 })} data-testid="filter-ueberweiser-select" />
        <label className="flex min-h-[44px] items-center gap-2"><input type="checkbox" className="h-5 w-5 accent-[var(--kd-primary)]" checked={f.nur_meine} onChange={(e) => setF({ ...f, nur_meine: e.target.checked, seite: 1 })} data-testid="filter-nur-meine" />Nur meine</label>
        <label className="text-sm text-text-muted">Eingang von<DateInputDE value={f.von} onChange={(v) => setF({ ...f, von: v, seite: 1 })} data-testid="filter-von-input" /></label>
        <label className="text-sm text-text-muted">bis<DateInputDE value={f.bis} onChange={(v) => setF({ ...f, bis: v, seite: 1 })} data-testid="filter-bis-input" /></label>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Status filtern">
        {Object.entries(STATUS).map(([k, l]) => (
          <button key={k} type="button" aria-pressed={f.status.includes(k)} onClick={() => toggle(k)} data-testid={`filter-status-${k}`}
            className={`min-h-[36px] rounded-full border px-3 text-sm transition-colors ${f.status.includes(k) ? "border-primary bg-primary text-white" : "border-border bg-surface text-text hover:border-primary"}`}>{l}</button>
        ))}
        {(f.status.length > 0 || f.suche || f.dringlichkeit || f.ueberweiser_id || f.von || f.bis || f.nur_meine) && (
          <button type="button" className="min-h-[36px] px-2 text-sm text-primary underline" onClick={() => setF(EMPTY)} data-testid="filter-reset">Filter zurücksetzen</button>
        )}
      </div>
      </div>
    </Card>
  );
}

function Wv({ c }: { c: any }) {
  if (!c.wiedervorlage_am) return null;
  return <span className={`mt-1 block w-fit rounded-full px-2 py-0.5 text-xs font-medium ${c.wv_faellig ? "border border-warning bg-warning-bg text-[#7A3E00]" : "bg-bg text-text-muted"}`} title={c.wiedervorlage_notiz || ""} data-testid={`wv-${c.fallnummer}`}>
    <CalendarDays className="mr-1 inline h-3 w-3" aria-hidden />{c.wv_faellig ? "Wiedervorlage fällig" : "Wiedervorlage"} {fmtDate(c.wiedervorlage_am)}</span>;
}

function Unread({ n }: { n: number }) {
  if (!n) return <span className="text-text-muted">–</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-sm font-semibold text-white" aria-label={`${n} ungelesene Nachrichten`}><MessageSquare className="h-3.5 w-3.5" aria-hidden />{n}</span>;
}

function Table({ items }: { items: any[] }) {
  const nav = useNavigate();
  return (
    <div className="kd-card hidden overflow-x-auto md:block">
      <table className="w-full text-left text-sm" data-testid="cases-table">
        <thead className="border-b border-border bg-bg text-text-muted">
          <tr>{["Fallnummer", "Eingang", "Patient", "Überweiser", "Grund", "Dringlichkeit", "Status", "Zuständig", "Nachr."].map((h) => <th key={h} className="whitespace-nowrap px-3 py-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} onClick={() => nav(`/praxis/faelle/${c.id}`)} className="cursor-pointer border-b border-border last:border-0 hover:bg-primary-light" data-testid={`case-row-${c.fallnummer}`}>
              <td className="whitespace-nowrap px-3 py-3 font-medium"><Link to={`/praxis/faelle/${c.id}`} onClick={(e) => e.stopPropagation()}>{c.fallnummer}</Link><Wv c={c} /></td>
              <td className="whitespace-nowrap px-3 py-3">{fmtDateTime(c.erstellt_am)}</td>
              <td className="px-3 py-3">{c.patient}</td>
              <td className="px-3 py-3">{c.ueberweiser}{c.unverifiziert && <span className="mt-1 inline-block rounded-full border border-warning bg-warning-bg px-2 py-0.5 text-xs font-medium text-[#7A3E00]" data-testid={`unverified-${c.fallnummer}`}>Überweiser nicht verifiziert</span>}{c.ist_test && <span className="ml-1 mt-1 inline-block text-xs text-text-muted">Testfall</span>}</td>
              <td className="px-3 py-3">{c.grund}</td>
              <td className="px-3 py-3"><UrgencyBadge value={c.dringlichkeit} /></td>
              <td className="px-3 py-3"><StatusBadge status={c.status} /></td>
              <td className="px-3 py-3">{c.zustaendig || "–"}</td>
              <td className="px-3 py-3"><Unread n={c.ungelesen} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CardsMobile({ items }: { items: any[] }) {
  return (
    <ul className="space-y-3 md:hidden" data-testid="cases-cards">
      {items.map((c) => (
        <li key={c.id}>
          <Link to={`/praxis/faelle/${c.id}`} className="kd-card block p-4 text-text no-underline" data-testid={`case-card-${c.fallnummer}`}>
            <div className="mb-2 flex items-center justify-between gap-2"><span className="font-semibold">{c.fallnummer}</span><Unread n={c.ungelesen} /></div>
            <Wv c={c} />
            <p className="font-medium">{c.patient}</p>
            <p className="text-sm text-text-muted">{c.grund} · {c.ueberweiser}</p>
            <p className="mb-2 text-sm text-text-muted">{fmtDateTime(c.erstellt_am)}</p>
            <div className="flex flex-wrap gap-2"><UrgencyBadge value={c.dringlichkeit} /><StatusBadge status={c.status} /></div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function CaseList() {
  const [k, setK] = useState<any>(null);
  const [uebs, setUebs] = useState<any[]>([]);
  const [f, setF] = useState<any>(EMPTY);
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/praxis/kennzahlen").then((r) => setK(r.data)).catch(() => undefined);
    api.get("/praxis/ueberweiser-optionen").then((r) => setUebs(r.data)).catch(() => undefined);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const body = { ...f, dringlichkeit: f.dringlichkeit || null, ueberweiser_id: f.ueberweiser_id || null, von: f.von || null, bis: f.bis || null, suche: f.suche || null };
      api.post("/praxis/faelle/liste", body).then((r) => { setRes(r.data); setErr(""); }).catch((e) => setErr(errMsg(e)));
    }, f.suche ? 300 : 0);
    return () => clearTimeout(t);
  }, [f]);

  return (
    <div className="kd-fade">
      <h1 className="mb-5">Eingangsliste</h1>
      <Kpis k={k} />
      <Filters f={f} setF={setF} uebs={uebs} />
      <ErrorBox>{err}</ErrorBox>
      {!res ? <Spinner /> : res.items.length === 0 ? (
        <Card><p className="text-text-muted" data-testid="cases-empty">Keine Fälle gefunden.</p></Card>
      ) : (
        <>
          <Table items={res.items} />
          <CardsMobile items={res.items} />
          <div className="mt-4 flex items-center justify-between gap-3" data-testid="pagination">
            <p className="text-sm text-text-muted">{res.gesamt} Fälle · Seite {res.seite} von {res.seiten}</p>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={res.seite <= 1} onClick={() => setF({ ...f, seite: f.seite - 1 })} aria-label="Vorherige Seite" data-testid="page-prev"><ChevronLeft className="h-4 w-4" aria-hidden /></Button>
              <Button variant="secondary" disabled={res.seite >= res.seiten} onClick={() => setF({ ...f, seite: f.seite + 1 })} aria-label="Nächste Seite" data-testid="page-next"><ChevronRight className="h-4 w-4" aria-hidden /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
